import { DOCUMENT } from '@angular/common';
import {
    DestroyRef,
    Injectable,
    Injector,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService, UserStore } from '@quipex/core/auth';
import {
    RealtimeService,
    type JobProgressMessage,
} from '@quipex/core/realtime';
import type {
    SelectedDocumentExportAcceptance,
    SelectedDocumentExportHandoff,
} from '@quipex/feature/building-context';
import { BuildingDocumentsApiService } from '@quipex/shared/contracts';
import { combineLatest, distinctUntilChanged, map, Subject, tap } from 'rxjs';
import {
    activityIdentitiesEqual,
    createEmptyActivitySession,
    type ActivityIdentity,
    type ActivityIntent,
    type ActivityKey,
} from '../activity-journal';
import { activityIdentityForLoadedContext } from '../activity-journal/models';
import {
    activityIntentFor,
    deriveActivitySections,
    deriveAttentionCount,
} from '../activity-journal/policy';
import { reduceActivity } from '../activity-journal/reducer';
import {
    clearActivitySession,
    clearActivitySessionsForUser,
    loadActivitySession,
    saveActivitySession,
} from '../activity-journal/storage';
import {
    activityActionSnapshotsEqual,
    type ActivityActionSnapshot,
    type ActivityToastDecisionSnapshot,
} from './action-contract';
import { watchActivityConnectionRecovery } from './connection-recovery';
import { observeActivityJob } from './job-observer';
import {
    SelectedExportRecovery,
    watchSelectedExportRecovery,
} from './selected-export-recovery';
import { ActivityToastOwner } from './toast-owner';

@Injectable()
export class ActivityState implements SelectedDocumentExportHandoff {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    private readonly auth = inject(AuthService);
    private readonly realtime = inject(RealtimeService);
    private readonly toastOwner = inject(ActivityToastOwner);
    private readonly selectedInjector = inject(Injector);
    private readonly userStore = inject(UserStore);
    //#endregion

    //#region State
    private readonly identity = signal<ActivityIdentity | null>(null);
    private readonly identityVersion = signal(0);
    private readonly session = signal(createEmptyActivitySession());
    private readonly toastDecisionSubject =
        new Subject<ActivityToastDecisionSnapshot>();
    private readonly storage = readSessionStorage(this.document);
    private lastAuthenticatedUserId: string | null = null;
    private logoutHasStarted = false;
    private readonly selectedRecovery = new SelectedExportRecovery({
        identity: this.identity,
        generation: this.identityVersion,
        items: () => this.items(),
        session: this.session,
        apply: (action) => this.apply(action),
        markUnavailable: (key) => this.markUnavailable(key),
        markStale: (key) => this.markStale(key),
        api: () => injectApi(this.selectedInjector),
        save: (session) => {
            this.session.set(session);
            this.persist();
        },
        toast: (decision) => this.toastDecisionSubject.next(decision),
    });

    public readonly activeIdentity = this.identity.asReadonly();
    public readonly activeIdentityVersion = this.identityVersion.asReadonly();
    public readonly items = computed(() => this.session().items);
    public readonly dismissalTombstones = computed(
        () => this.session().dismissalTombstones
    );
    public readonly sections = computed(() =>
        deriveActivitySections(this.items())
    );
    public readonly attentionCount = computed(() =>
        deriveAttentionCount(this.items())
    );
    public readonly connectionStatus = computed(() => ({
        state: this.realtime.connectionState(),
        updatesDelayed: this.realtime.connectionState() !== 'connected',
    }));
    public readonly toastDecisions$ = this.toastDecisionSubject.asObservable();
    //#endregion

    constructor() {
        this.watchIdentity();
        this.watchLiveJobs();
        watchSelectedExportRecovery(
            this.selectedRecovery,
            this.realtime.connectionState,
            this.destroyRef,
            this.document
        );
        watchActivityConnectionRecovery({
            destroyRef: this.destroyRef,
            identity: this.identity,
            connectionState: this.realtime.connectionState,
            markStale: () => this.markStale(),
            markStaleItemsUnavailable: () => this.markStaleItemsUnavailable(),
        });
        this.auth.logoutStarted$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.deactivateForLogout());
    }

    //#region Public methods
    public capture(): SelectedDocumentExportAcceptance | null {
        return this.selectedRecovery.capture();
    }

    public markAttentionSeen(buildingPublicId: string | null = null): void {
        this.apply({ type: 'mark-attention-seen', buildingPublicId });
    }

    public intentFor(key: ActivityKey): ActivityIntent | null {
        return this.actionFor(key)?.intent ?? null;
    }

    public actionFor(key: ActivityKey): ActivityActionSnapshot | null {
        const identity = this.identity();
        const item = this.items().find((candidate) => candidate.key === key);
        const intent = item ? activityIntentFor(item) : null;
        return identity && item && intent
            ? {
                  identity,
                  activeIdentityVersion: this.identityVersion(),
                  intent,
                  version: item.updatedAtUtc,
              }
            : null;
    }

    public markActedOn(
        key: ActivityKey,
        expectedAction?: ActivityActionSnapshot
    ): void {
        if (
            expectedAction &&
            !activityActionSnapshotsEqual(this.actionFor(key), expectedAction)
        ) {
            return;
        }

        this.apply({ type: 'mark-acted-on', key });
    }

    public dismiss(key: ActivityKey): void {
        this.apply({ type: 'dismiss', key });
    }

    public markStale(key?: ActivityKey): void {
        this.apply({ type: 'mark-stale', key });
    }

    public markUnavailable(
        key: ActivityKey,
        expectedAction?: ActivityActionSnapshot
    ): void {
        if (
            expectedAction &&
            !activityActionSnapshotsEqual(this.actionFor(key), expectedAction)
        ) {
            return;
        }

        this.apply({ type: 'mark-unavailable', key });
    }

    public markBuildingUnavailable(buildingPublicId: string): void {
        const matchingKeys = this.items()
            .filter((item) =>
                item.jobs.some(
                    (job) => job.buildingPublicId === buildingPublicId
                )
            )
            .map((item) => item.key);

        for (const key of matchingKeys) this.apply({ type: 'quarantine', key });
    }

    public clear(): void {
        const identity = this.identity();
        if (identity) clearActivitySession(this.storage, identity);

        this.session.set(createEmptyActivitySession());
    }
    //#endregion

    //#region Private methods
    private watchIdentity(): void {
        combineLatest([this.userStore.me$, this.userStore.org$])
            .pipe(
                tap(([user]) => {
                    if (user) this.lastAuthenticatedUserId = user.id;
                }),
                map(([user, organisation]) =>
                    activityIdentityForLoadedContext(
                        this.identity(),
                        this.logoutHasStarted ? null : (user?.id ?? null),
                        organisation?.id ?? null
                    )
                ),
                distinctUntilChanged(activityIdentitiesEqual),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((identity) => this.activateIdentity(identity));
    }

    private watchLiveJobs(): void {
        this.realtime.jobProgress$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((message) => this.observeLiveJob(message));
    }

    private activateIdentity(identity: ActivityIdentity | null): void {
        this.toastOwner.invalidateAll();
        this.identityVersion.update((version) => version + 1);
        this.selectedRecovery.clear();
        this.identity.set(identity);
        const restored = identity
            ? loadActivitySession(this.storage, identity)
            : createEmptyActivitySession();
        this.session.set(
            identity
                ? reduceActivity(restored, { type: 'mark-stale' })
                : restored
        );
        this.selectedRecovery.refreshSelectedJobs();
    }

    private markStaleItemsUnavailable(): void {
        for (const item of this.items()) {
            if (
                item.availability === 'stale' &&
                !item.jobs.some(
                    (job) => job.jobType === 'SelectedBuildingDocumentExport'
                )
            )
                this.markUnavailable(item.key);
        }
    }

    private deactivateForLogout(): void {
        this.logoutHasStarted = true;
        if (this.lastAuthenticatedUserId) {
            clearActivitySessionsForUser(
                this.storage,
                this.lastAuthenticatedUserId
            );
        }

        this.lastAuthenticatedUserId = null;
        if (this.identity() !== null) {
            this.toastOwner.invalidateAll();
            this.identityVersion.update((version) => version + 1);
            this.identity.set(null);
        }
        this.session.set(createEmptyActivitySession());
    }

    private observeLiveJob(message: JobProgressMessage): void {
        const identity = this.identity();
        if (!identity) return;

        if (this.selectedRecovery.observeRealtime(message)) return;
        const previousSession = this.session();
        const observation = observeActivityJob({
            identity,
            activeIdentityVersion: this.identityVersion(),
            session: previousSession,
            message,
        });
        if (!observation) return;

        if (observation.session !== previousSession) {
            this.session.set(observation.session);
            this.persist();
        }

        if (observation.toastDecision)
            this.toastDecisionSubject.next(observation.toastDecision);
    }

    private apply(action: Parameters<typeof reduceActivity>[1]): void {
        const previous = this.session();
        const next = reduceActivity(previous, action);
        if (next === previous) return;

        this.session.set(next);
        this.persist();
    }

    private persist(): void {
        const identity = this.identity();
        if (identity)
            saveActivitySession(this.storage, identity, this.session());
    }

    //#endregion
}

function readSessionStorage(document: Document): Storage | null {
    try {
        return document.defaultView?.sessionStorage ?? null;
    } catch {
        return null;
    }
}

function injectApi(injector: Injector): BuildingDocumentsApiService {
    return injector.get(BuildingDocumentsApiService);
}
